import { useEffect, useRef } from "react";
import { createTimeline, stagger, onScroll } from "animejs";
import useReducedMotion from "../../hooks/useReducedMotion";

export default function TimelineEntry({ entry, side, index }) {
	const rootRef = useRef(null);
	const dotRef = useRef(null);
	const cardRef = useRef(null);
	const dateRef = useRef(null);
	const roleRef = useRef(null);
	const orgRef = useRef(null);
	const reduced = useReducedMotion();

	useEffect(() => {
		const root = rootRef.current;
		const card = cardRef.current;
		const dot = dotRef.current;
		if (!root || !card || !dot) return;

		const isHorizontal = window.matchMedia("(min-width: 801px)").matches;

		if (isHorizontal) {
			// Set initial hidden state
			if (!reduced) {
				dot.style.opacity = "0";
				dot.style.transform = "scale(0)";
				card.style.opacity = "0";
				const yFrom = side === "left" ? -18 : 18;
				card.style.transform = `translateY(${yFrom}px)`;
				[dateRef, roleRef, orgRef].forEach((r) => {
					if (r.current) {
						r.current.style.opacity = "0";
						r.current.style.transform = "translateY(10px)";
					}
				});
			}

			if (reduced) {
				dot.style.opacity = "1";
				card.style.opacity = "1";
				return;
			}

			// Use the horizontal scroll container as IntersectionObserver root
			const scrollContainer = root.closest(".tl-root");
			let animated = false;

			const io = new IntersectionObserver(
				([entry]) => {
					if (entry.isIntersecting && !animated) {
						animated = true;
						io.unobserve(root);

						const yFrom = side === "left" ? -18 : 18;
						const tl = createTimeline({ defaults: { ease: "outQuad" } });
						tl.add(dot, {
							scale: [0, 1],
							opacity: [0, 1],
							duration: 520,
							ease: "outBack(1.7)",
						})
							.add(
								card,
								{
									translateY: [yFrom, 0],
									opacity: [0, 1],
									duration: 560,
								},
								"<+=100",
							)
							.add(
								[
									dateRef.current,
									roleRef.current,
									orgRef.current,
								].filter(Boolean),
								{
									translateY: [10, 0],
									opacity: [0, 1],
									duration: 380,
									delay: stagger(60),
								},
								"<+=80",
							);
					}
				},
				{
					root: scrollContainer ?? null,
					threshold: 0.4,
				},
			);

			io.observe(root);
			return () => io.disconnect();
		}

		// ---- Vertical / mobile path ----
		if (!reduced) {
			[dot, card].forEach((el) => (el.style.opacity = "0"));
			[dateRef, roleRef, orgRef].forEach((r) => {
				if (r.current) {
					r.current.style.opacity = "0";
					r.current.style.transform = "translateY(10px)";
				}
			});
		}

		if (reduced) {
			[dot, card].forEach((el) => (el.style.opacity = "1"));
			return;
		}

		let scrollObserver;
		try {
			scrollObserver = onScroll({
				container: window,
				target: root,
				enter: "bottom-=15% top",
				leave: "top bottom",
				sync: false,
			});
		} catch (_) {
			scrollObserver = undefined;
		}

		const xFrom = side === "left" ? -40 : 40;
		const tl = createTimeline({
			autoplay: scrollObserver ?? true,
			defaults: { ease: "outQuad" },
		});
		tl.add(dot, {
			scale: [0, 1],
			opacity: [0, 1],
			duration: 520,
			ease: "outBack(1.7)",
		})
			.add(
				card,
				{ translateX: [xFrom, 0], opacity: [0, 1], duration: 600 },
				"<+=100",
			)
			.add(
				[dateRef.current, roleRef.current, orgRef.current].filter(Boolean),
				{
					translateY: [10, 0],
					opacity: [0, 1],
					duration: 400,
					delay: stagger(65),
				},
				"<+=80",
			);

		return () => tl.pause?.();
	}, [side, reduced]);

	const accentVar = `var(--${entry.accent}-color)`;

	return (
		<li
			className={`tl-entry tl-entry--${side}`}
			ref={rootRef}
			style={{ "--entry-accent": accentVar }}
		>
			<div className="tl-entry__rail">
				<span className="tl-entry__dot" ref={dotRef} />
				<span className="tl-entry__tick" aria-hidden="true" />
			</div>
			<article className="tl-entry__card" ref={cardRef}>
				<div className="tl-entry__date mono" ref={dateRef}>
					<span className="tl-entry__index">
						{String(index + 1).padStart(2, "0")}
					</span>
					{entry.date}
				</div>
				<h3 className="tl-entry__role" ref={roleRef}>
					{entry.role}
				</h3>
				<p className="tl-entry__org" ref={orgRef}>
					<span className="tl-entry__org-name">{entry.org}</span>
					<span className="tl-entry__org-sep"> · </span>
					<span className="tl-entry__loc mono">{entry.location}</span>
				</p>
				{entry.bullets?.length > 0 && (
					<ul className="tl-entry__bullets">
						{entry.bullets.map((b, i) => (
							<li key={i}>{b}</li>
						))}
					</ul>
				)}
			</article>
		</li>
	);
}
